
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleServiceNavigation = (path: string) => {
    setIsServicesDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  const handleServicesClick = () => {
    if (isServicesDropdownOpen) {
      navigate('/services');
      setIsServicesDropdownOpen(false);
    } else {
      setIsServicesDropdownOpen(true);
    }
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { 
      label: 'Services', 
      path: '/services',
      hasDropdown: true,
      dropdownItems: [
        { label: 'Mortgage Refinance', path: '/services/mortgage-refinance', icon: 'ri-home-4-line' },
        { label: 'Pre-underwriting AI', path: '/services/preunderwriting-ai', icon: 'ri-robot-2-line' },
        { label: 'Debt Settlement', path: '/services/debt-settlement', icon: 'ri-money-dollar-circle-line' },
        { label: 'Inbound Calls', path: '/services/inbound-calls', icon: 'ri-phone-line' },
        { label: 'Health Insurance', path: '/services/health-insurance', icon: 'ri-health-book-line' },
        { label: 'Home Security', path: '/services/home-security', icon: 'ri-shield-keyhole-line' }
      ]
    },
    { label: 'About', path: '/about' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/contact' }
  ];

  return (
    <header className={`backdrop-blur-sm shadow-lg sticky top-0 z-[9999] border-b border-gray-200/30 relative overflow-visible transition-all duration-300 ${isScrolled ? 'bg-white/95' : 'bg-transparent'}`}>
      <nav className="px-6 py-4 relative z-10">
        <div className="flex justify-between items-center">
          <div 
            onClick={() => navigate('/')}
            className="flex items-center cursor-pointer group"
          >
            <img 
              src="https://static.readdy.ai/image/90f9aac22cdad51829432cf34fb4cd7d/b7b6dfa86f5fb9b0b93a19a7e790014c.png"
              alt="Halcom Analytics"
              className="h-14 w-auto mr-3 group-hover:scale-110 transition-transform"
            />
          </div>

          {/* Email Display - Centered between logo and navigation */}
          <div className="hidden lg:flex items-center text-gray-800 font-medium text-base hover:text-cyan-600 transition-colors cursor-pointer">
            <i className="ri-mail-line mr-2 text-cyan-600 text-lg"></i>
            <span>info@halcomanalytics.com</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.path} className="relative">
                {item.hasDropdown ? (
                  <div 
                    className="relative group"
                    onMouseEnter={() => setIsServicesDropdownOpen(true)}
                    onMouseLeave={() => setIsServicesDropdownOpen(false)}
                  >
                    <button
                      onClick={handleServicesClick}
                      className="text-gray-800 hover:text-cyan-600 font-medium transition-colors cursor-pointer relative group flex items-center whitespace-nowrap"
                    >
                      {item.label}
                      <i className={`ri-arrow-down-s-line ml-1 text-sm transition-transform ${isServicesDropdownOpen ? 'rotate-180' : ''}`}></i>
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-600 transition-all group-hover:w-full"></span>
                    </button>
                    
                    {/* Dropdown Menu */}
                    <div className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-4 z-[99999] transition-all duration-300 transform ${isServicesDropdownOpen ? 'opacity-100 visible translate-y-0 scale-100' : 'opacity-0 invisible -translate-y-4 scale-95'}`} style={{ zIndex: 99999 }}>
                      <div className="px-4 pb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Our Services</p>
                      </div>
                      {item.dropdownItems?.map((dropdownItem) => (
                        <button
                          key={dropdownItem.path}
                          onClick={() => handleServiceNavigation(dropdownItem.path)}
                          className="w-full text-left px-6 py-3 hover:bg-cyan-50 transition-all duration-200 group flex items-center whitespace-nowrap hover:translate-x-1"
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-cyan-100 rounded-lg mr-3 group-hover:bg-cyan-600 transition-all duration-200">
                            <i className={`${dropdownItem.icon} text-cyan-600 group-hover:text-white text-sm`}></i>
                          </div>
                          <span className="text-gray-700 group-hover:text-cyan-600 font-medium">
                            {dropdownItem.label}
                          </span>
                        </button>
                      ))}
                      <div className="px-6 pt-2 mt-2 border-t border-gray-100">
                        <button
                          onClick={() => handleServiceNavigation('/services')}
                          className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center whitespace-nowrap"
                        >
                          <i className="ri-arrow-right-line mr-2"></i>
                          View All Services
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate(item.path)}
                    className="text-gray-800 hover:text-cyan-600 font-medium transition-colors cursor-pointer relative group whitespace-nowrap"
                  >
                    {item.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-600 transition-all group-hover:w-full"></span>
                  </button>
                )}
              </div>
            ))}
            
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 transform hover:scale-105 transition-all shadow-lg border-0 whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer bg-gray-100 backdrop-blur-sm rounded-lg hover:bg-gray-200 transition-colors"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-800`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-6 py-6 border-t border-gray-200 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg z-[99999]" style={{ zIndex: 99999 }}>
            {navItems.map((item) => (
              <div key={item.path}>
                <button
                  onClick={() => {
                    if (item.hasDropdown) {
                      setIsServicesDropdownOpen(!isServicesDropdownOpen);
                    } else {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  className="block w-full text-left py-3 px-4 text-gray-800 hover:text-cyan-600 hover:bg-gray-50 font-medium transition-colors cursor-pointer rounded-lg mx-2 whitespace-nowrap flex items-center justify-between"
                >
                  <span>{item.label}</span>
                  {item.hasDropdown && (
                    <i className={`ri-arrow-down-s-line transition-transform ${isServicesDropdownOpen ? 'rotate-180' : ''}`}></i>
                  )}
                </button>
                {item.hasDropdown && isServicesDropdownOpen && (
                  <div className="ml-6 mt-2 space-y-1 pb-2">
                    {item.dropdownItems?.map((dropdownItem) => (
                      <button
                        key={dropdownItem.path}
                        onClick={() => handleServiceNavigation(dropdownItem.path)}
                        className="block w-full text-left py-2 px-4 text-gray-700 hover:text-cyan-600 hover:bg-gray-50 transition-colors cursor-pointer rounded-lg flex items-center whitespace-nowrap"
                      >
                        <i className={`${dropdownItem.icon} text-cyan-600 mr-3`}></i>
                        {dropdownItem.label}
                      </button>
                    ))}
                    <button
                      onClick={() => handleServiceNavigation('/services')}
                      className="block w-full text-left py-2 px-4 text-cyan-600 hover:text-cyan-700 font-medium transition-colors cursor-pointer rounded-lg flex items-center whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-line mr-3"></i>
                      View All Services
                    </button>
                  </div>
                )}
              </div>
            ))}
            
            {/* Mobile Email Display */}
            <div className="px-6 py-3 border-t border-gray-200 mt-4">
              <div className="flex items-center text-gray-800 font-medium text-base">
                <i className="ri-mail-line mr-2 text-cyan-600 text-lg"></i>
                <span>info@halcomanalytics.com</span>
              </div>
            </div>

            <div className="mt-6 px-2">
              <Button 
                onClick={() => {
                  navigate('/contact');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 border-0 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
